import {
  AvailabilityDetailedSlotByIdDocument,
  BillingOptions,
  CheckCouponValidityQueryResult,
  PaymentType,
  SpecialityChannel,
  UserInclinicPayment,
} from '@/types-aggregatore';
import { ClinicAllowedPayments } from '@/store/createBookingInformationsSlice';
import { CheckoutFormFields, FormStep } from '@/store/createCheckoutSlice';
import client from './apolloClient';

export const formatPrice = ({ amount, minimumFractionDigits = 2 }: { amount?: null | number; minimumFractionDigits?: number }) => {
  if (amount === null || amount === undefined) {
    console.error('amount is null or undefined');
    return '';
  }
  if (isNaN(amount)) {
    console.error('amount is NaN');
    return '';
  }
  return (amount / 100)
    .toLocaleString('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits,
    })
    .replace(/\s/g, ' ')
    .trim();
};

export type CouponResponse = {
  amountSubtracted: number;
  newTotalAmount: number;
};

export const getClinicPaymentOptions = (availablePayments: PaymentType[]): ClinicAllowedPayments => {
  return {
    online: availablePayments?.includes(PaymentType.Online),
    onsite: availablePayments?.includes(PaymentType.Onsite),
    deferred: availablePayments?.includes(PaymentType.Deferred),
  };
};

export const calculateCouponDiscount = (
  coupon: CheckCouponValidityQueryResult['checkCouponValidity'],
  totalAmount: number,
): CouponResponse | null => {
  let amountToSubtract = 0;
  if (coupon?.fixed) {
    amountToSubtract = coupon.fixed;
  } else if (coupon?.percentage) {
    amountToSubtract = Math.floor((totalAmount / 10000) * coupon.percentage);
  }
  if (amountToSubtract > totalAmount) {
    return {
      amountSubtracted: amountToSubtract,
      newTotalAmount: 0,
    };
  } else {
    return {
      amountSubtracted: amountToSubtract,
      newTotalAmount: totalAmount - amountToSubtract,
    };
  }
};

/**
 * Generate a URL for the checkout page with the necessary parameters based on the input values.
 *
 * @param availabilitySlotId String representing the ID of the availability slot
 * @returns String representing the URL for the checkout page with the necessary parameters based on the input values.
 **/
type CheckoutUrlInput = Record<string, string>;
export const getCheckoutURL = (pathname: string, input: CheckoutUrlInput) => {
  const searchParams = new URLSearchParams(input);
  return `${pathname}?${searchParams.toString()}`;
};

export const getAllowedPayments = (allowedPayments: ClinicAllowedPayments) => {
  return Object.entries(allowedPayments).reduce<string[]>((acc, [key, value]) => {
    if (!value) return acc;
    acc.push(key);
    return acc;
  }, []);
};

type FetchCheckoutDataInput = {
  availabilitySlotId: string;
  // ! TODO improve this type, there are more ts errors around the code
  checkoutFormStep: FormStep;
  setBookingInformation: Function;
  updateCheckoutFormField: <K extends keyof CheckoutFormFields>(field: K, value: CheckoutFormFields[K]) => void;
  setBookingInformationLoading: (isLoading: boolean) => void;
};

export const fetchCheckoutData = async ({
  availabilitySlotId,
  checkoutFormStep,
  setBookingInformation,
  updateCheckoutFormField,
  setBookingInformationLoading,
}: FetchCheckoutDataInput) => {
  if (availabilitySlotId && checkoutFormStep !== FormStep.BookingConfirmation) {
    setBookingInformation({
      slot: undefined,
      clinicAllowedPayments: undefined,
      reservationOrigin: undefined,
    });

    const { data, error } = await client.query({
      query: AvailabilityDetailedSlotByIdDocument,
      variables: { id: availabilitySlotId },
    });

    const slot = data?.availabilityDetailedSlotById;
    if (!slot || error) {
      throw new Error('Slot not found');
    }

    const clinicAllowedPayments = getClinicPaymentOptions(slot?.workgroup?.availablePayments ?? []);
    setBookingInformation({
      slot,
      clinicAllowedPayments: clinicAllowedPayments,
      reservationOrigin: undefined,
    });
    updateCheckoutFormField(
      'homeVisit',
      slot?.speciality?.specialityChannels?.includes(SpecialityChannel.Online) ? false : (slot.workgroup?.domiciliaryCare ?? false),
    );
    // The 730 should be checked by default only if include730Required is true, otherwise it should be undefined
    updateCheckoutFormField('includeIn730', slot?.workgroup?.include730Required === true ? true : undefined);

    updateCheckoutFormField('isVideoConsultation', slot?.speciality?.specialityChannels?.includes(SpecialityChannel.Online) ?? false);
    updateCheckoutFormField('userPaymentSelected', UserInclinicPayment.Onsite);
    updateCheckoutFormField('totalAmount', slot.slotPrice ?? slot.basePrice ?? 0);
    setBookingInformationLoading(false);
  }
};

export const getUserBillingInfo = (checkoutForm: CheckoutFormFields, billingOptions?: BillingOptions, isPaymentOnline?: boolean) => {
  if ((billingOptions === BillingOptions.OnlineOnly && !isPaymentOnline) || billingOptions === BillingOptions.NotRequired) {
    return null;
  }

  if (
    checkoutForm?.billingDistrict &&
    checkoutForm?.billingCity &&
    checkoutForm?.billingStreet &&
    checkoutForm?.billingStreetNumber &&
    checkoutForm?.billingCap
  ) {
    return {
      district: checkoutForm.billingDistrict,
      city: checkoutForm.billingCity,
      street: checkoutForm.billingStreet,
      streetNumber: checkoutForm.billingStreetNumber,
      cap: checkoutForm.billingCap,
    };
  } else if (
    checkoutForm.isDomiciliarySameAsBillingAddress &&
    checkoutForm?.homeVisitDistrict &&
    checkoutForm?.homeVisitCity &&
    checkoutForm?.homeVisitStreet &&
    checkoutForm?.homeVisitStreetNumber &&
    checkoutForm?.homeVisitCap
  ) {
    return {
      district: checkoutForm.homeVisitDistrict,
      city: checkoutForm.homeVisitCity,
      street: checkoutForm.homeVisitStreet,
      streetNumber: checkoutForm.homeVisitStreetNumber,
      cap: checkoutForm.homeVisitCap,
    };
  } else {
    return null;
  }
};
